/** An individual tab of the "horizontal nav" tab list.
 * Generic top component delegating to the appropriate tab type.
 */
import React, { forwardRef, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { SimpleNavigationTab } from '@atlassian/jira-business-tabs/src/ui/simple-navigation-tab/index.tsx';
import {
	useHideNavigationItem,
	type HideNavigationItemArgs,
} from '@atlassian/jira-navigation-kit-hide-item-service-old/src/services/index.tsx';
import type { JiraNavigationItemTypeKey } from '@atlassian/jira-relay/src/__generated__/ui_navigationKitTabListOld_TabList.graphql';
import type { jiraTab_navigationKitTabListOld$key } from '@atlassian/jira-relay/src/__generated__/jiraTab_navigationKitTabListOld.graphql';

type Props = {
	tabFragment: jiraTab_navigationKitTabListOld$key;
	position: number;
	id: string;
	beforeItemId: string | null;
	afterItemId: string | null;
	defaultViewTypeKey?: JiraNavigationItemTypeKey;
};

const JiraTab = forwardRef(
	(
		{ tabFragment, position, id, beforeItemId, afterItemId, defaultViewTypeKey }: Props,
		ref: React.Ref<HTMLElement>,
	) => {
		const tabData = useFragment(
			graphql`
				fragment jiraTab_navigationKitTabListOld on JiraNavigationItem {
					__typename
					...simpleNavigationTab_businessTabs
				}
			`,
			tabFragment,
		);

		const hideNavigationItem = useHideNavigationItem();
		const hideTab = useCallback(
			(args: HideNavigationItemArgs) => {
				hideNavigationItem({ ...args });
			},
			[hideNavigationItem],
		);

		switch (tabData?.__typename) {
			case 'JiraWorkManagementSavedView':
				return (
					<SimpleNavigationTab
						tabFragment={tabData}
						position={position}
						id={id}
						onHideTab={hideTab}
						beforeItemId={beforeItemId}
						afterItemId={afterItemId}
						defaultViewTypeKey={defaultViewTypeKey}
						ref={ref}
					/>
				);
			default:
				return null;
		}
	},
);

export default JiraTab;
